import './LinearPacing.scss';
import { CSSProperties, useCallback, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdOutlineMenuBook } from 'react-icons/md';
import * as Dfns from 'date-fns/fp';
import { useIsFetching } from '@tanstack/react-query';
import { BreadcrumbTitle } from '@Components/BreadcrumbTitle';
import { Button } from '@Components/Button';
import { ColorKey, KeyShape } from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { DatePicker } from '@Components/DatePicker';
import { Dropdown } from '@Components/Dropdown/Dropdown';
import { Background, Design, Placement, PlacementType } from '@Components/designConstants';
import { GlossaryItem, GlossaryModal } from '@Components/GlossaryModal/GlossaryModal';
import { Page } from '@Components/Page';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { LAYER_HEIGHT, MARKER_OVERHANG } from '@Pages/Pacing/components/PacingBar';
import { PacingBarColors } from '@src/utils/colors';
import { isNotMonday } from '@src/utils/date-utils';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { LinearPacingDetail2 } from './LinearPacing2/LinearPacingDetail2';
import { SUMMARY_QUERY_KEY } from './LinearPacing2/use-linear-pacing-campaign';
import { CLEARANCE_QUERY_KEY, FORECAST_QUERY_KEY } from './use-linear-clearance';
import { DETAIL_QUERY_KEY } from './use-linear-pacing';
import {
  ACCEPTED_LABEL,
  ACCEPTED_OPTIONS,
  AcceptedDimension,
  COMPANY_DIMENSION,
  DATE_FORMAT,
  DEFAULT_WEEKS_BACK,
  MEDIA_TYPE_ALL,
  MEDIA_TYPE_OPTIONS,
  MediaTypeDimension,
  NAVS,
  NET_LABEL,
  PRIMARY_DIMENSION_OPTIONS,
  PrimaryDimension,
  SPEND_OPTIONS,
  SpendDimension,
  TAB_BY_CAMPAIGN,
  TAB_CLEARANCE,
  TAB_CLEARANCE_VS_FORECAST,
  TAB_CLIENT_DETAIL
} from './linear-pacing-constants';
import { LinearPacingClearance } from './LinearPacingClearance';
import { LinearPacingDetail } from './LinearPacingDetail';
import { LinearPacingForecast } from './LinearPacingForecast';

interface LegendEntry {
  color: string;
  shape: KeyShape;
  label: string;
  placement: PlacementType;
  tooltip: string;
}

const MEDIA_TYPE_DROPDOWN_OPTIONS = MEDIA_TYPE_OPTIONS.map(value => ({ label: value, value }));

// Lets the SCSS size rows and logos from the shared PacingBar's dimensions.
// Cast, not `satisfies`: CSSProperties has no index signature for custom --vars.
const BAR_SIZE_VARS = {
  '--pacing-bar-height': `${LAYER_HEIGHT}px`,
  '--pacing-bar-margin': `${MARKER_OVERHANG}px`
} as CSSProperties;

// Delivery layers are keyed as squares and target markers as lines, matching the bars.
const LEGEND: LegendEntry[] = [
  {
    color: PacingBarColors.deliveryUnsecured,
    shape: 'square',
    label: 'On the logs (unsecured)',
    placement: Placement.RIGHT,
    tooltip: 'The sum of unsecured dollars reflected in pre-logs and/or electronic detections.'
  },
  {
    color: PacingBarColors.deliverySecured,
    shape: 'square',
    label: 'On the logs (secured)',
    placement: Placement.BOTTOM,
    tooltip: 'The sum of secured dollars reflected in pre-logs and/or electronic detections.'
  },
  {
    color: PacingBarColors.deliveryUpfront,
    shape: 'square',
    label: 'On the logs (upfront)',
    placement: Placement.BOTTOM,
    tooltip:
      'The sum of upfront dollars (secured spots bought in the upfront) reflected in pre-logs and/or electronic detections.'
  },
  {
    color: PacingBarColors.pacing,
    shape: 'square',
    label: 'Pacing',
    placement: Placement.BOTTOM,
    tooltip:
      'Total estimated clearance. Where we have pre-logs and/or electronic detection, we take that as read; where we have neither, we assume a clearance rate based on the historical trend for that network/avail.'
  },
  {
    color: PacingBarColors.booked,
    shape: 'line',
    label: 'Booked',
    placement: Placement.BOTTOM,
    tooltip: 'Total amount of booked media.'
  },
  {
    color: PacingBarColors.clientTarget,
    shape: 'line',
    label: 'Client Target',
    placement: Placement.BOTTOM,
    tooltip: 'The spend target for the week as directed by the client.'
  },
  {
    color: PacingBarColors.bookedTarget,
    shape: 'line',
    label: 'Booked Target',
    placement: Placement.BOTTOM,
    tooltip:
      'The IMPLIED spend target based on the amount of media we’ve booked and the clearance assumptions we’ve made. Any material divergence from Client Target signals a departure from booking strategy.'
  }
];

// Only By Campaign splits upfront out of secured, so Client Detail has no upfront bar to key.
const CLIENT_DETAIL_LEGEND = LEGEND.filter(
  ({ color }) => color !== PacingBarColors.deliveryUpfront
);

// The legend's own explanations, for the Glossary (the only definitions the page has).
const GLOSSARY: GlossaryItem[] = LEGEND.map(({ label, tooltip }) => ({
  term: label,
  definition: tooltip
}));

// Each tab's primary query; the panes' secondary queries load inline.
const PRIMARY_QUERY_KEYS: unknown[] = [
  DETAIL_QUERY_KEY,
  SUMMARY_QUERY_KEY,
  CLEARANCE_QUERY_KEY,
  FORECAST_QUERY_KEY
];

const formatDate = Dfns.format(DATE_FORMAT);
const thisWeek = Dfns.startOfISOWeek(new Date());

export const LinearPacing = () => {
  const isLoading =
    useIsFetching({ predicate: query => PRIMARY_QUERY_KEYS.includes(query.queryKey[0]) }) > 0;
  const [selectedView, setSelectedView] = useState(TAB_CLIENT_DETAIL);
  const [selectedLeft, setSelectedLeft] = useState<string>();
  const [selectedRight, setSelectedRight] = useState<string>();
  const [showGlossary, setShowGlossary] = useState(false);
  const [spendDimension, setSpendDimension] = useState<SpendDimension>(NET_LABEL);
  const [acceptedDimension, setAcceptedDimension] = useState<AcceptedDimension>(ACCEPTED_LABEL);
  const [mediaTypeDimension, setMediaTypeDimension] = useState<MediaTypeDimension>(MEDIA_TYPE_ALL);
  const [primaryDimension, setPrimaryDimension] = useState<PrimaryDimension>(COMPANY_DIMENSION);
  const [detailStart, setDetailStart] = useState(formatDate(thisWeek));
  const [clearanceStartDate, setClearanceStartDate] = useState(
    formatDate(Dfns.subWeeks(DEFAULT_WEEKS_BACK, thisWeek))
  );
  // Last week's clearance isn't complete until Thursday.
  const [clearanceEndDate, setClearanceEndDate] = useState(
    formatDate(Dfns.subWeeks(Dfns.getISODay(new Date()) >= 4 ? 1 : 2, thisWeek))
  );

  const clearSelection = useCallback(() => {
    setSelectedLeft(undefined);
    setSelectedRight(undefined);
  }, []);

  const isClearanceView =
    selectedView === TAB_CLEARANCE || selectedView === TAB_CLEARANCE_VS_FORECAST;

  const actions = useMemo(
    () =>
      isClearanceView ? (
        <DatePicker
          range={{ start: clearanceStartDate, end: clearanceEndDate }}
          fullWeeksOnly
          isOutsideRange={() => false}
          isDayBlocked={isNotMonday}
          onChange={({ start, end }) => {
            if (start && end) {
              setClearanceStartDate(start);
              setClearanceEndDate(end);
            }
          }}
        />
      ) : (
        <div className='linear-pacing-actions'>
          <SegmentedControlButton
            background={Background.DARK}
            options={PRIMARY_DIMENSION_OPTIONS}
            selectedOption={primaryDimension}
            onChange={dimension => {
              setPrimaryDimension(dimension);
              clearSelection();
            }}
          />
          <Dropdown
            label='Media Type'
            value={mediaTypeDimension}
            options={MEDIA_TYPE_DROPDOWN_OPTIONS}
            onChange={setMediaTypeDimension}
          />
          <DatePicker
            isSingle
            fullWeeksOnly
            range={{ start: detailStart, end: detailStart }}
            isOutsideRange={() => false}
            isDayBlocked={isNotMonday}
            onChange={({ start }) => {
              if (start) {
                setDetailStart(start);
              }
            }}
          />
        </div>
      ),
    [
      isClearanceView,
      clearanceStartDate,
      clearanceEndDate,
      primaryDimension,
      mediaTypeDimension,
      detailStart,
      clearSelection
    ]
  );

  const detailProps = {
    spendDimension,
    acceptedDimension,
    mediaTypeDimension,
    leftKey: primaryDimension,
    start: detailStart,
    selectedLeft,
    setSelectedLeft,
    selectedRight,
    setSelectedRight
  };

  const breadcrumbs = useMemo(
    () => [
      ...(selectedLeft
        ? [{ key: 'left', label: selectedLeft, onClick: () => setSelectedRight(undefined) }]
        : []),
      ...(selectedRight ? [{ key: 'right', label: selectedRight }] : [])
    ],
    [selectedLeft, selectedRight]
  );

  return (
    <Page
      title={
        <BreadcrumbTitle
          title={PAGE_NAMES.LINEAR_PACING}
          path={breadcrumbs}
          onTitleClick={clearSelection}
        />
      }
      pageType={PAGE_NAMES.LINEAR_PACING}
      navs={NAVS}
      selectedNav={selectedView}
      onNav={view => {
        setSelectedView(view);
        clearSelection();
      }}
      actions={actions}
      subHeader={
        !isClearanceView && (
          <div className='linear-pacing-sub-header'>
            <SegmentedControlButton
              design={Design.NEUTRAL}
              options={SPEND_OPTIONS}
              selectedOption={spendDimension}
              onChange={setSpendDimension}
            />
            <SegmentedControlButton
              design={Design.NEUTRAL}
              options={ACCEPTED_OPTIONS}
              selectedOption={acceptedDimension}
              onChange={setAcceptedDimension}
            />
            <div className='pacing-legend' data-testid='pacing-legend'>
              {(selectedView === TAB_CLIENT_DETAIL ? CLIENT_DETAIL_LEGEND : LEGEND).map(
                ({ color, shape, label, placement, tooltip }) => (
                  <OverlayTrigger
                    key={label}
                    placement={placement}
                    overlay={<Tooltip>{tooltip}</Tooltip>}
                  >
                    <div className='legend-group'>
                      <ColorKey shape={shape} color={color} />
                      {label}
                    </div>
                  </OverlayTrigger>
                )
              )}
            </div>
            <Button
              design={Design.NEUTRAL}
              leadingElement={<MdOutlineMenuBook />}
              onClick={() => {
                Mixpanel.track(MxE.ModalOpened, {
                  page: PAGE_NAMES.LINEAR_PACING,
                  modal: 'pacing glossary'
                });
                setShowGlossary(true);
              }}
            >
              Glossary
            </Button>
          </div>
        )
      }
      isLoading={isLoading}
    >
      <div className='linear-pacing' data-testid='linear-pacing' style={BAR_SIZE_VARS}>
        {selectedView === TAB_CLIENT_DETAIL && <LinearPacingDetail {...detailProps} />}
        {selectedView === TAB_CLEARANCE && (
          <LinearPacingClearance
            startDate={clearanceStartDate}
            endDate={clearanceEndDate}
            selectedLeft={selectedLeft}
            setSelectedLeft={setSelectedLeft}
            selectedRight={selectedRight}
            setSelectedRight={setSelectedRight}
          />
        )}
        {selectedView === TAB_CLEARANCE_VS_FORECAST && (
          <LinearPacingForecast startDate={clearanceStartDate} endDate={clearanceEndDate} />
        )}
        {selectedView === TAB_BY_CAMPAIGN && <LinearPacingDetail2 {...detailProps} />}
      </div>
      <GlossaryModal
        show={showGlossary}
        setShow={setShowGlossary}
        title='Pacing Glossary'
        initialGlossary={GLOSSARY}
      />
    </Page>
  );
};
